<script setup>
import { ref, computed } from 'vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import FeesConfigForm from '../fees/FeesConfigForm.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'

const feesData = ref(null)
const isLoading = ref(true)
const showConfigForm = ref(false)
const selectedTab = ref('delivery')

function onFetched(payload) {
  feesData.value = payload?.data || {}
  isLoading.value = false
}

function onStartGetting() {
  isLoading.value = true
}

function openConfigForm() {
  showConfigForm.value = true
}

function onSaved() {
  showConfigForm.value = false
  Events.dispatch(EVENTS.REFRESH_DATA_REQUESTED, 'fees')
}

const activeFees = computed(() => {
  if (!feesData.value) return {}
  return feesData.value
})
</script>

<template>
  <div class="p-6 h-full overflow-auto bg-gray-50/50">
    <!-- Header -->
    <div class="mb-8 flex justify-between items-end">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          Configuration Économique
        </h1>
        <p class="text-gray-500">
          Gérez dynamiquement tous les frais, commissions et bonus de la
          plateforme.
        </p>
      </div>
      <button
        @click="openConfigForm"
        class="px-6 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
      >
        <span>Modifier la configuration</span>
      </button>
    </div>

    <!-- Content Area -->
    <div class="max-w-5xl space-y-8">
      <!-- Livraison & Checkpoints -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-green-50 text-green-600 rounded-lg">📦</span>
          Livraison & Checkpoints
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <p class="text-xs font-bold text-gray-400 uppercase">Standards</p>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Frais de base</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.baseFee ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Coût / Kilomètre</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.pricePerKm ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Prix / Gramme</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.pricePerG ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Prix / Document</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.pricePerDoc ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Frais de Pickup</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.pickupFee ?? '-' }} FCFA</span
              >
            </div>
          </div>

          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <p class="text-xs font-bold text-gray-400 uppercase">
              Checkpoints & Seuils
            </p>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Checkpoint Fixe</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.checkPointFeeFixed ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Checkpoint %</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.delivery?.checkPointFeePercent ?? '-' }}%</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Livraison Gratuite</span>
              <span class="font-bold text-gray-900"
                >{{
                  activeFees.delivery?.freeDeliveryThreshold ?? '-'
                }}
                FCFA</span
              >
            </div>
          </div>

          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <p class="text-xs font-bold text-gray-400 uppercase">
              Frais de Service
            </p>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Pourcentage</span>
              <span class="font-bold text-primary"
                >{{ activeFees.service?.percent ?? '-' }}%</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Frais Minimum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.service?.minimumFee ?? '-' }} FCFA</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Commissions Restaurant & Marchand -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Restaurant -->
        <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
          <h3
            class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
          >
            <span class="p-2 bg-orange-50 text-orange-600 rounded-lg">🍽️</span>
            Commissions Restaurant
          </h3>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Commission Rapidoo (%)</span>
              <span class="font-bold text-primary"
                >{{
                  activeFees.restaurant?.commissionRapidooPercent ?? '-'
                }}%</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm"
                >Commission Rapidoo (Fixe)</span
              >
              <span class="font-bold text-gray-900"
                >{{
                  activeFees.restaurant?.commissionRapidooFixed ?? '-'
                }}
                FCFA</span
              >
            </div>
          </div>
        </div>

        <!-- Marchand -->
        <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
          <h3
            class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
          >
            <span class="p-2 bg-blue-50 text-blue-600 rounded-lg">🏪</span>
            Commissions Marchand
          </h3>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Commission Rapidoo (%)</span>
              <span class="font-bold text-primary"
                >{{
                  activeFees.merchant?.commissionRapidooPercent ?? '-'
                }}%</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm"
                >Commission Rapidoo (Fixe)</span
              >
              <span class="font-bold text-gray-900"
                >{{
                  activeFees.merchant?.commissionRapidooFixed ?? '-'
                }}
                FCFA</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Commissions Rider -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-purple-50 text-purple-600 rounded-lg">🛵</span>
          Commissions Livreur (Rider)
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm"
              >Commission sur Livraison (%)</span
            >
            <span class="font-bold text-primary"
              >{{ activeFees.rider?.commissionOnDeliveryPercent ?? '-' }}%</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm"
              >Commission sur Livraison (Fixe)</span
            >
            <span class="font-bold text-gray-900"
              >{{
                activeFees.rider?.commissionOnDeliveryFixed ?? '-'
              }}
              FCFA</span
            >
          </div>
        </div>
      </div>

      <!-- Frais de Retrait (Payout) Généraux -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-emerald-50 text-emerald-600 rounded-lg">💸</span>
          Frais de Retrait - Clients
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Pourcentage</span>
            <span class="font-bold text-primary"
              >{{ activeFees.payout?.percent ?? '-' }}%</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Frais Fix</span>
            <span class="font-bold text-gray-900"
              >{{ activeFees.payout?.fixedFee ?? '-' }} FCFA</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Appliquer sur la source</span>
            <span
              class="font-bold"
              :class="activeFees.payout?.applyFeeOnSrc ? 'text-green-600' : 'text-gray-500'"
            >
              {{ activeFees.payout?.applyFeeOnSrc ? 'Oui' : 'Non' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Frais de Retrait - Livreur (Payout Rider) -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-purple-50 text-purple-600 rounded-lg">🛵</span>
          Frais de Retrait - Livreur
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Pourcentage</span>
            <span class="font-bold text-primary"
              >{{ activeFees.payoutRider?.percent ?? '-' }}%</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Frais Fix</span>
            <span class="font-bold text-gray-900"
              >{{ activeFees.payoutRider?.fixedFee ?? '-' }} FCFA</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Appliquer sur la source</span>
            <span
              class="font-bold"
              :class="activeFees.payoutRider?.applyFeeOnSrc ? 'text-green-600' : 'text-gray-500'"
            >
              {{ activeFees.payoutRider?.applyFeeOnSrc ? 'Oui' : 'Non' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Frais de Retrait - Restaurant (Payout Restaurant) -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-orange-50 text-orange-600 rounded-lg">🍽️</span>
          Frais de Retrait - Restaurant
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Pourcentage</span>
            <span class="font-bold text-primary"
              >{{ activeFees.payoutRestaurant?.percent ?? '-' }}%</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Frais Fix</span>
            <span class="font-bold text-gray-900"
              >{{ activeFees.payoutRestaurant?.fixedFee ?? '-' }} FCFA</span
            >
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Appliquer sur la source</span>
            <span
              class="font-bold"
              :class="activeFees.payoutRestaurant?.applyFeeOnSrc ? 'text-green-600' : 'text-gray-500'"
            >
              {{ activeFees.payoutRestaurant?.applyFeeOnSrc ? 'Oui' : 'Non' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Limites de Montants pour Retraits (Amount Limits) -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-yellow-50 text-yellow-600 rounded-lg">📊</span>
          Limites de Montant pour Retraits (FCFA)
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Client Payout Limits -->
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <p class="text-xs font-bold text-gray-400 uppercase">
              Retrait Client
            </p>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Montant Minimum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.amountLimits?.payout?.min ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Montant Maximum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.amountLimits?.payout?.max ?? '-' }} FCFA</span
              >
            </div>
          </div>

          <!-- Rider Payout Limits -->
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <p class="text-xs font-bold text-gray-400 uppercase">
              Retrait Livreur
            </p>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Montant Minimum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.amountLimits?.payoutRider?.min ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Montant Maximum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.amountLimits?.payoutRider?.max ?? '-' }} FCFA</span
              >
            </div>
          </div>

          <!-- Restaurant Payout Limits -->
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-3"
          >
            <p class="text-xs font-bold text-gray-400 uppercase">
              Retrait Restaurant
            </p>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Montant Minimum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.amountLimits?.payoutRestaurant?.min ?? '-' }} FCFA</span
              >
            </div>
            <div class="flex justify-between items-baseline">
              <span class="text-gray-600 text-sm">Montant Maximum</span>
              <span class="font-bold text-gray-900"
                >{{ activeFees.amountLimits?.payoutRestaurant?.max ?? '-' }} FCFA</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Fournisseurs de Paiement (Payment Providers) -->
      <div class="bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
        <h3
          class="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2"
        >
          <span class="p-2 bg-indigo-50 text-indigo-600 rounded-lg">🔌</span>
          Fournisseurs de Paiement
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Fournisseur de collecte</span>
            <span class="font-bold text-gray-900 uppercase">
              {{ activeFees.paymentProviders?.collectionProviders ?? '-' }}
            </span>
          </div>
          <div
            class="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-baseline"
          >
            <span class="text-gray-600 text-sm">Fournisseur de retrait</span>
            <span class="font-bold text-gray-900 uppercase">
              {{ activeFees.paymentProviders?.withdrawalProviders ?? '-' }}
            </span>
          </div>
        </div>
      </div>

    </div>

    <!-- Config Form Slide-over -->
    <SlideDetailView :isVisible="showConfigForm" @hide="showConfigForm = false">
      <FeesConfigForm
        v-if="feesData"
        :feesData="feesData"
        @saved="onSaved"
        @cancel="showConfigForm = false"
      />
    </SlideDetailView>

    <!-- Hidden fetcher -->
    <div class="hidden">
      <DataFetcher
        dataKey="fees"
        @fetched="onFetched"
        @startGetting="onStartGetting"
      />
    </div>
  </div>
</template>

<style scoped>
.animate-in {
  animation: animate-in 0.3s ease-out;
}

@keyframes animate-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>